import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
	hasActiveStreamFilters,
	parseReportStreamParams,
	ReportStreamParamOptions,
	ReportStreamState,
	StreamSort,
	writeReportStreamParams,
} from "../utils/reporting/reportStream";
import { ReportStatusKey } from "../utils/reporting/reportStatusOptions";

// How long typing pauses before the search box writes `?q=`.
const SEARCH_DELAY_MS = 250;

// A report stream's list kept in the page's address: its search, status
// figures, filters, sort, page and page size, so Back from a report brings
// the list back and a copied address opens the same list. Each change is a
// new history entry, which Back undoes, and goes back to the first page,
// except paging. The search box shows what's typed at once and writes it
// once typing pauses, back on the first page and replacing the entry, as
// Help Centre's search does (useSearchQueryParam); a change made meanwhile
// writes it too. Any other navigation drops what's still being typed.
// `options` must keep its identity between renders.
export function useReportStreamParams(options: ReportStreamParamOptions) {
	const [searchParams, setSearchParams] = useSearchParams();
	const { key: locationKey } = useLocation();
	const state = useMemo(
		() => parseReportStreamParams(searchParams, options),
		[searchParams, options]
	);
	// What's typed but not yet in the address.
	const [draft, setDraft] = useState<string | null>(null);
	const pendingSearch = useRef<string | null>(null);
	const timer = useRef<number>();
	// The text the last own write took, written or replaced, so the effect
	// below can tell its own navigation apart and clear the box's copy.
	const ownWrite = useRef<{ typed: string | null } | null>(null);

	const write = useCallback(
		(patch: Partial<ReportStreamState>, replace = false) => {
			window.clearTimeout(timer.current);
			const typed = pendingSearch.current;
			pendingSearch.current = null;
			const next = writeReportStreamParams(
				searchParams,
				{
					...parseReportStreamParams(searchParams, options),
					...(typed === null ? {} : { search: typed }),
					page: 1,
					...patch,
				},
				options
			);
			if (next.toString() === searchParams.toString()) {
				setDraft(null);
				return;
			}
			ownWrite.current = { typed };
			setSearchParams(next, { replace });
		},
		[searchParams, setSearchParams, options]
	);

	// The timer calls the write of the latest render.
	const latestWrite = useRef(write);
	useEffect(() => {
		latestWrite.current = write;
	}, [write]);

	const setSearchInput = useCallback((value: string) => {
		setDraft(value);
		pendingSearch.current = value;
		window.clearTimeout(timer.current);
		timer.current = window.setTimeout(
			() => latestWrite.current({ search: value }, true),
			SEARCH_DELAY_MS
		);
	}, []);

	useEffect(() => {
		const own = ownWrite.current;
		ownWrite.current = null;
		if (own) {
			// Keep only text typed since the write, with its timer.
			setDraft((typed) =>
				typed === own.typed || typed === state.search ? null : typed
			);
			return;
		}
		window.clearTimeout(timer.current);
		pendingSearch.current = null;
		setDraft(null);
	}, [locationKey, state.search]);

	useEffect(() => () => window.clearTimeout(timer.current), []);

	return {
		state,
		// What the search box shows.
		searchInput: draft ?? state.search,
		setSearchInput,
		filtersActive: hasActiveStreamFilters(state, options),
		setStatuses: (statuses: ReportStatusKey[]) => write({ statuses }),
		setFilters: (filters: Record<string, string[]>) =>
			write({ filters: { ...state.filters, ...filters } }),
		setSort: (sort: StreamSort | undefined) => write({ sort }),
		setPage: (page: number) => write({ page }),
		setPageSize: (pageSize: number) => write({ pageSize }),
		// The search, figures and filters back to the defaults; the sort and
		// the page size stay.
		reset: () =>
			write({
				search: "",
				statuses: [...options.defaultStatuses],
				filters: {},
			}),
	};
}
